import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  Box,
  Chip,
  Typography,
  CircularProgress,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Tooltip
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Role } from '../../types';
import { invitationService, BulkInviteResult } from '../../services/invitation/invitation.service';
import { roleService } from '../../services';
import { logError } from '../../utils/error-handler';

// ─── Types ───────────────────────────────────────────────────────────────────

interface ParsedEmail {
  value: string;
  valid: boolean;
}

export interface BulkInviteDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after the results screen is dismissed so the parent can refresh its list. */
  onComplete: (results: BulkInviteResult) => void;
  organizationId: string;
  /** When provided the invite is scoped to this team. */
  teamId?: string;
  /** Used in the dialog title. */
  teamName?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmailInput(raw: string): ParsedEmail[] {
  return raw
    .split(/[\n,;]+/)
    .map(s => s.trim())
    .filter(Boolean)
    .map(value => ({ value, valid: EMAIL_RE.test(value) }));
}

function downloadCsvTemplate() {
  const csv = 'email\nexample1@company.com\nexample2@company.com\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'bulk-invite-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface EmailChipListProps {
  emails: ParsedEmail[];
  onRemove: (index: number) => void;
}

const EmailChipList: React.FC<EmailChipListProps> = ({ emails, onRemove }) => {
  if (emails.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
      {emails.map((e, i) => (
        <Chip
          key={i}
          label={e.value}
          size="small"
          color={e.valid ? 'default' : 'error'}
          variant={e.valid ? 'outlined' : 'filled'}
          onDelete={() => onRemove(i)}
          title={e.valid ? e.value : `"${e.value}" is not a valid email`}
        />
      ))}
    </Box>
  );
};

interface ResultsSummaryProps {
  results: BulkInviteResult;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({ results }) => {
  const [showDetails, setShowDetails] = useState(false);
  const hasFailures = results.failed_invitations.length > 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {results.successful_count > 0 && (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
            {results.successful_count} invitation{results.successful_count !== 1 ? 's' : ''} sent successfully
          </Alert>
        )}
        {results.failed_count > 0 && (
          <Alert severity="error" icon={<ErrorOutlineIcon />}>
            {results.failed_count} invitation{results.failed_count !== 1 ? 's' : ''} failed
          </Alert>
        )}
        {results.successful_count === 0 && results.failed_count === 0 && (
          <Alert severity="warning" icon={<WarningAmberIcon />}>
            No invitations were sent
          </Alert>
        )}
      </Box>

      {hasFailures && (
        <>
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
            onClick={() => setShowDetails(v => !v)}
          >
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
              Show details
            </Typography>
            <IconButton size="small">
              {showDetails ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={showDetails}>
            <List dense disablePadding>
              {results.successful_invitations.map((item, i) => (
                <ListItem key={`s-${i}`} disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.invitee_email}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
              {results.failed_invitations.map((item, i) => (
                <ListItem key={`f-${i}`} disableGutters>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <ErrorOutlineIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.invitee_email}
                    secondary={item.error}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ORG_ASSIGNABLE_SYSTEM_ROLES = [
  'OrgAdmin', 'OrgMember', 'TeamAdmin', 'ContentManager', 'ContentPublisher', 'ContentReader'
];

const BulkInviteDialog: React.FC<BulkInviteDialogProps> = ({
  open,
  onClose,
  onComplete,
  organizationId,
  teamId,
  teamName
}) => {
  // ── Form state
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState<ParsedEmail[]>([]);
  const [roleId, setRoleId] = useState('');
  const [message, setMessage] = useState('');
  const [roleError, setRoleError] = useState('');

  // ── Roles
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);

  // ── Submission
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [results, setResults] = useState<BulkInviteResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Load roles when dialog opens
  useEffect(() => {
    if (!open) return;
    setIsLoadingRoles(true);

    const load = teamId
      ? roleService.getRoles({ teamId, level: 'team' })
      : roleService.getRoles({ organizationId });

    load
      .then(all => {
        const filtered = teamId
          ? (all as any[])
          : (all as any[]).filter(r =>
              (r.level === 'organization' && r.organizationId === organizationId) ||
              (r.level === 'system' && ORG_ASSIGNABLE_SYSTEM_ROLES.includes(r._id))
            );
        setRoles(filtered);

        // Default to lowest-privilege role.
        // Org context: prefer OrgMember, then ContentReader.
        // Team context: prefer ContentReader, then Member (case-insensitive).
        const orgPreference = ['OrgMember', 'ContentReader'];
        const teamPreference = ['ContentReader', 'Member'];
        const preference = teamId ? teamPreference : orgPreference;

        const def =
          preference.reduce<any>(
            (found, name) => found || filtered.find((r: any) => r.name.toLowerCase() === name.toLowerCase()),
            null
          ) ||
          filtered.find((r: any) => r.isDefault) ||
          filtered[filtered.length - 1];  // last = lowest when API returns highest-first
        if (def) setRoleId(def._id);
      })
      .catch(err => logError('Failed to load roles', err))
      .finally(() => setIsLoadingRoles(false));
  }, [open, organizationId, teamId]);

  // ── Reset when dialog opens/closes
  useEffect(() => {
    if (open) {
      setEmailInput('');
      setEmails([]);
      setMessage('');
      setRoleError('');
      setResults(null);
      setSubmitError(null);
    }
  }, [open]);

  // ── Parse email textarea into chips whenever text changes
  const handleEmailInputChange = (value: string) => {
    setEmailInput(value);
    // Only commit to chips when there are delimiters (comma, semicolon, newline)
    if (/[,;\n]/.test(value)) {
      const parsed = parseEmailInput(value);
      if (parsed.length > 0) {
        setEmails(prev => {
          const existing = new Set(prev.map(e => e.value.toLowerCase()));
          const deduped = parsed.filter(p => !existing.has(p.value.toLowerCase()));
          return [...prev, ...deduped];
        });
        setEmailInput('');
      }
    }
  };

  // Commit any remaining text when the textarea loses focus
  const handleEmailInputBlur = () => {
    if (!emailInput.trim()) return;
    const parsed = parseEmailInput(emailInput);
    if (parsed.length > 0) {
      setEmails(prev => {
        const existing = new Set(prev.map(e => e.value.toLowerCase()));
        const deduped = parsed.filter(p => !existing.has(p.value.toLowerCase()));
        return [...prev, ...deduped];
      });
      setEmailInput('');
    }
  };

  const handleRemoveEmail = (index: number) => {
    setEmails(prev => prev.filter((_, i) => i !== index));
  };

  // ── File upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = evt => {
      const text = evt.target?.result as string;
      // Strip any leading header line that contains no '@' (e.g. "email", "Email Address")
      const withoutHeader = text.replace(/^[^\n@]*\n/, (line) =>
        line.trim().toLowerCase() === 'email' || !line.includes('@') ? '' : line
      );
      const parsed = parseEmailInput(withoutHeader);
      setEmails(prev => {
        const existing = new Set(prev.map(e => e.value.toLowerCase()));
        const deduped = parsed.filter(p => !existing.has(p.value.toLowerCase()));
        return [...prev, ...deduped];
      });
    };
    reader.readAsText(file);
    // Reset file input so the same file can be re-selected
    e.target.value = '';
  };

  // ── Validation
  const validEmails = emails.filter(e => e.valid);
  const invalidEmails = emails.filter(e => !e.valid);
  const canSubmit = validEmails.length > 0 && !!roleId && !isSubmitting;

  const validate = (): boolean => {
    if (!roleId) {
      setRoleError('Please select a role');
      return false;
    }
    setRoleError('');
    return true;
  };

  // ── Submit
  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const result = await invitationService.bulkInvite(
        organizationId,
        validEmails.map(e => e.value),
        roleId,
        teamId,
        message.trim() || undefined
      );
      setResults(result);
    } catch (err: any) {
      setSubmitError(err?.message || 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDone = () => {
    if (results) onComplete(results);
    onClose();
  };

  // ── Title
  const title = teamId
    ? `Invite Members to ${teamName || 'Team'}`
    : 'Invite Members';

  // ── Render results screen
  if (results) {
    return (
      <Dialog open={open} onClose={handleDone} maxWidth="sm" fullWidth>
        <DialogTitle>Invitation Results</DialogTitle>
        <DialogContent>
          <ResultsSummary results={results} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDone} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // ── Render input screen
  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      {isSubmitting && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}

      <DialogContent>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {/* Email input */}
        <Typography variant="subtitle2" gutterBottom>
          Email Addresses
        </Typography>
        <TextField
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          placeholder="Enter one email per line, or separate with commas"
          value={emailInput}
          onChange={e => handleEmailInputChange(e.target.value)}
          onBlur={handleEmailInputBlur}
          disabled={isSubmitting}
          size="small"
        />

        {/* File upload row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain,application/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
          >
            Upload CSV / TXT
          </Button>
          <Tooltip title="Download a sample CSV template">
            <Button size="small" onClick={downloadCsvTemplate} disabled={isSubmitting}>
              Download template
            </Button>
          </Tooltip>
        </Box>

        {/* Email chips */}
        {emails.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {validEmails.length} valid
              {invalidEmails.length > 0 && `, ${invalidEmails.length} invalid (will be skipped)`}
            </Typography>
            <EmailChipList emails={emails} onRemove={handleRemoveEmail} />
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Role selector */}
        <FormControl fullWidth size="small" error={!!roleError} disabled={isSubmitting || isLoadingRoles}>
          <InputLabel id="bulk-role-label">Role</InputLabel>
          <Select
            labelId="bulk-role-label"
            value={roleId}
            label="Role"
            onChange={e => {
              setRoleId(e.target.value as string);
              if (roleError) setRoleError('');
            }}
          >
            {isLoadingRoles ? (
              <MenuItem disabled>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  Loading roles…
                </Box>
              </MenuItem>
            ) : roles.length > 0 ? (
              roles.map((r: any) => (
                <MenuItem key={r._id} value={r._id}>{r.name}</MenuItem>
              ))
            ) : (
              <MenuItem disabled>No roles available</MenuItem>
            )}
          </Select>
          {roleError && <FormHelperText>{roleError}</FormHelperText>}
        </FormControl>

        {/* Optional message */}
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          size="small"
          label="Personal message (optional)"
          value={message}
          onChange={e => setMessage(e.target.value)}
          disabled={isSubmitting}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSubmitting
            ? 'Sending…'
            : `Send ${validEmails.length > 0 ? validEmails.length : ''} Invitation${validEmails.length !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkInviteDialog;
